import { ElementProps } from '@/components/sections/builder/types';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import clsx from 'clsx';
import { ProductDetailTitleElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Currency } from 'lucide-react';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/vanilla/shallow';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { IProduct, ProductItem } from '@/types/product';
import { cn, formatIDR } from '@/lib/utils';
import React from 'react';
import { useProductStore } from '@/stores/product';

const defaultData: ProductDetailTitleElementDataType = {
  name: 'Price',
  label: '10000',
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;
  const parentData = (element.parentData || {}) as IProduct;
  const productStore = useProductStore((state) => state, shallow);

  const getMinMaxPrice = (productItems: ProductItem[]) => {
    const prices = productItems
      ?.map((item) => item.sell_price)
      .filter((price) => typeof price === 'number' && !isNaN(price)) as number[];

    if (!prices || prices.length === 0) return { minPrice: null, maxPrice: null };
    return { minPrice: Math.min(...prices), maxPrice: Math.max(...prices) };
  };

  const { minPrice, maxPrice } = getMinMaxPrice(parentData.product_items || []);

  const calculatePrice = () => {
    if ((parentData.variants?.length ?? 0) > 0) {
      if (minPrice !== maxPrice) {
        return productStore.price
          ? formatIDR(productStore.price)
          : `${formatIDR(minPrice as number)} - ${formatIDR(maxPrice as number)}`;
      }
      return formatIDR(minPrice as number);
    } else {
      return formatIDR(parentData.product_items?.[0].sell_price as number);
    }
  };

  const calculateDiscountPrice = () => {
    if ((parentData.variants?.length ?? 0) > 0) {
      return productStore.discountPrice;
    }
    return parentData.discount_price;
  };

  const price = calculatePrice();
  const discountPrice = calculateDiscountPrice();

  return (
    <>
      <div data-animation='true' {...getOverrideAnimationParams(data.animation)}>
        <div className={clsx(discountPrice !== 0 && 'flex flex-col items-start')}>
          <span
            className={cn(
              'font-bold',
              data.textStyle === 'body' && 'text-lg md:text-xl lg:text-2xl',
              (data.textStyle === 'subtitle' || !data.textStyle) &&
                'text-base md:text-lg lg:text-xl',
              data.textStyle === 'uppercase' && 'text-base uppercase md:text-lg lg:text-xl',
              discountPrice !== 0 ? 'text_product_secondary' : 'text_product_primary'
            )}
          >
            {discountPrice !== 0 ? formatIDR(discountPrice as number) ?? 0 : price ?? 0}
          </span>
          {discountPrice !== 0 ? (
            <span className='text-[10px] font-normal text-[#C2C2C2] line-through sm:text-sm'>
              {price}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
};

export const ElementPreviewIcon = Currency;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 font-bold'>Text Style</div>
      <InputTab
        defaultValue={data.textStyle || 'subtitle'}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            textStyle: v,
          })
        }
      />
    </React.Fragment>
  );
}

const ProductDetailPrice = {
  type: 'price',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailPrice;
